import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

/**
 * Validator function that ensures the 'confirm_email' field matches the 'email' field.
 * Should be applied at the FormGroup level.
 *
 * @returns A ValidatorFn that returns a validation error if emails don't match,
 *          or null if they match or if either field is empty.
 *
 * @example
 * ```typescript
 * const form = new FormGroup({
 *   email: new FormControl(''),
 *   confirm_email: new FormControl('')
 * }, { validators: confirmEmailValidator() });
 * ```
 */
export function confirmEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormGroup)) {
      return null;
    }

    const emailControl = control.get('email');
    const confirmEmailControl = control.get('confirm_email');

    if (!emailControl || !confirmEmailControl) {
      return null;
    }

    const email = (emailControl.value as string)?.trim() ?? '';
    const confirmEmail = (confirmEmailControl.value as string)?.trim() ?? '';

    // Only validate if both fields have values
    if (!email || !confirmEmail) {
      return null;
    }

    // If emails don't match, set error on confirmEmail field
    if (email !== confirmEmail) {
      const existingErrors = confirmEmailControl.errors ?? {};
      confirmEmailControl.setErrors({
        ...existingErrors,
        mismatch: true,
      });
      return { emailMismatch: true };
    }

    // Emails match - clear mismatch error if it exists
    if (confirmEmailControl.errors?.['mismatch']) {
      const remainingErrors = Object.fromEntries(
        Object.entries(confirmEmailControl.errors).filter(
          ([key]) => key !== 'mismatch'
        )
      );
      if (Object.keys(remainingErrors).length === 0) {
        confirmEmailControl.setErrors(null);
      } else {
        confirmEmailControl.setErrors(remainingErrors);
      }
    }

    return null;
  };
}
